import { useGroups } from '@entities/common/groups/index.ts';
import { useGroupsSelecting } from '@entities/common/groups/model.ts';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicationType } from '@widgets/shared/Publication/types.ts';
import { useLoadGroups } from '@features/common/index.ts';
import { areArraysWithNumbersEqual } from '@shared/helpers/index.ts';
import { useCloseFormDialogModal } from '@shared/hooks/useCloseFormDialogModal/useCloseFormDialogModal.ts';
import type { Group } from '@shared/types/common';
import { useAppDispatch } from '@src/library/hooks/common/index.ts';
import { openNotifyModal } from '@src/library/redux/modal/index.ts';

const showBackendErrorNotifyNotify = () => {
  return openNotifyModal({
    needClose: false,
    title: 'Публикация',
    text: 'Возникли технические неполадки, обратитесь в поддержку',
    confirmText: 'Ок',
  });
};

const showNotEnoughSelectedNotify = () => {
  return openNotifyModal({
    title: 'Уведомление',
    text: `Для публикации необходимо выбрать хотя бы одну из существующих групп`,
    confirmText: 'Ок',
  });
};

const showUnPublishedSuccessNotify = ({ entityName }: { entityName: string }) => {
  return openNotifyModal({
    title: 'Публикация',
    text: `${entityName} успешно снята с публикации`,
    confirmText: 'Ок',
  });
};

const showPublishedSuccessNotify = ({
  is_published,
  entityName,
}: {
  entityName: string;
  is_published: boolean;
}) => {
  return openNotifyModal({
    needClose: is_published ? false : undefined,
    title: 'Публикация',
    text: is_published
      ? 'Условия публикации успешно изменены'
      : `${entityName} успешно опубликована`,
    confirmText: 'Ок',
  });
};

export const usePublication = ({
  is_published,
  is_for_all,
  is_for_future_groups,
  is_for_specialists,
  is_for_without_groups,
  is_for_not_clients,
  custom_groups,
  excluded_custom_groups,
  onUpdatePublication,
  entityName,
}: Partial<PublicationType> & {
  entityName: string;
  onUpdatePublication: (
    data: Partial<PublicationType>
  ) => Promise<{ data?: PublicationType; error?: FetchBaseQueryError | SerializedError }>;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasMore: hasMoreGroups, loadGroups, status: groupsLoaderStatus } = useLoadGroups(true);
  const { groups: initGroups } = useGroups();
  const {
    selectedGroupsIds,
    resetSelectedGroupsIds,
    setIsSelectingTrue,
    setIsSelectingFalse,
    isSelecting,
  } = useGroupsSelecting();
  const { doCloseFormWithDialogModal } = useCloseFormDialogModal();

  const [specialistsGroupData, setSpecialistsGroupsData] = useState<Group | undefined>();
  const [withoutGroupGroupData, setWithoutGroupGroupData] = useState<Group | undefined>();

  const [isInitStateHasChanged, setIsInitStateHasChanged] = useState(false);
  const [isShouldShowReadyButton, setIsShouldShowReadyButton] = useState(false);

  const [isForAll, setIsForAll] = useState(Boolean(is_for_all));
  const [isPublished, setIsPublished] = useState(Boolean(is_published));
  const [isForFutureGroups, setIsForFutureGroups] = useState(Boolean(is_for_future_groups));
  const [isForNotClient, setIsForNotClient] = useState(Boolean(is_for_not_clients));

  const groups = useMemo(
    () =>
      initGroups.filter((group) => {
        if (group.special_type === 'coaches') {
          setSpecialistsGroupsData(group);
          return false;
        }
        if (group.special_type === 'without_group') {
          setWithoutGroupGroupData(group);
          return false;
        }
        return true;
      }),
    [initGroups, setSpecialistsGroupsData, setWithoutGroupGroupData]
  );

  // Если выбраны "для будущих групп", тогда мы выбираем все группы, кроме тех, что в ексклюдед
  const initSelectedGroups = useMemo(
    () =>
      (is_for_future_groups
        ? initGroups?.filter((el) => !excluded_custom_groups?.includes(el.id)).map((el) => el.id)
        : custom_groups) || [],
    [is_for_future_groups, custom_groups, excluded_custom_groups, initGroups]
  );

  const isPublishButtonDisabled = useMemo(
    () => false,
    // (is_published && !isPublished) || !(selectedGroupsIds?.length || isForNotClient),
    [isForNotClient, selectedGroupsIds]
  );

  useEffect(() => {
    if (!isSelecting) {
      if (!is_published && selectedGroupsIds) {
        resetSelectedGroupsIds([]);
      }
      setIsSelectingTrue();
    }
    if (is_published) {
      // Если выбраны "для будущих групп", тогда мы выбираем все группы, кроме тех, что в ексклюдед
      resetSelectedGroupsIds(initSelectedGroups);
      setIsForFutureGroups(Boolean(is_for_future_groups));
      setIsForNotClient(Boolean(is_for_not_clients));
      setIsPublished(Boolean(is_published));
    }
  }, [
    setIsPublished,
    is_published,
    custom_groups,
    is_for_future_groups,
    is_for_not_clients,
    initSelectedGroups,
  ]);

  useEffect(() => {
    if (
      isPublished !== Boolean(is_published) ||
      isForAll !== Boolean(is_for_all) ||
      isForFutureGroups !== Boolean(is_for_future_groups) ||
      isForNotClient !== Boolean(is_for_not_clients) ||
      !areArraysWithNumbersEqual({
        firstArray: initSelectedGroups,
        secondArray: selectedGroupsIds,
      })
    ) {
      setIsInitStateHasChanged(true);
    } else {
      setIsInitStateHasChanged(false);
    }

    if (selectedGroupsIds?.length === initGroups.length && isForFutureGroups && isForNotClient) {
      setIsForAll(true);
    } else {
      setIsForAll(false);
    }
  }, [
    initSelectedGroups,
    isPublished,
    is_published,
    initGroups,
    is_for_all,
    is_for_specialists,
    is_for_without_groups,
    is_for_not_clients,
    is_for_future_groups,
    isForAll,
    isForFutureGroups,
    isForNotClient,
    selectedGroupsIds,
    custom_groups,
  ]);

  const onPageExit = useCallback(() => {
    navigate(-1);
    setTimeout(() => {
      setIsSelectingFalse();
      resetSelectedGroupsIds([]);
    }, 0);
  }, [navigate, resetSelectedGroupsIds, setIsSelectingFalse]);

  const doShowReadyButton = useCallback(
    () => !isShouldShowReadyButton && setIsShouldShowReadyButton(true),
    [setIsShouldShowReadyButton, isShouldShowReadyButton]
  );

  const onForAllClick = useCallback(() => {
    if (isForAll) {
      resetSelectedGroupsIds([]);
      setIsForAll(false);
      setIsForFutureGroups(false);
      setIsForNotClient(false);
    } else {
      resetSelectedGroupsIds(initGroups.map((el) => el.id));
      setIsForAll(true);
      setIsForFutureGroups(true);
      setIsForNotClient(true);
    }
    doShowReadyButton();
  }, [
    isForAll,
    resetSelectedGroupsIds,
    setIsForAll,
    initGroups,
    setIsForFutureGroups,
    setIsForNotClient,
    doShowReadyButton,
  ]);

  const onForFutureGroupsClick = useCallback(() => {
    setIsForFutureGroups((s) => !s);
    doShowReadyButton();
  }, [setIsForFutureGroups, doShowReadyButton]);

  const onForNotClientClick = useCallback(() => {
    setIsForNotClient((s) => !s);
    doShowReadyButton();
  }, [setIsForNotClient, doShowReadyButton]);

  const publish = useCallback(async () => {
    if (isForAll || isForNotClient || selectedGroupsIds.length) {
      const result = await onUpdatePublication({
        is_published: true,
        is_for_future_groups: isForFutureGroups,
        is_for_all: isForAll,
        is_for_not_clients: isForNotClient,
        excluded_custom_groups: groups
          .filter((el) => !selectedGroupsIds.includes(el.id))
          .map((el) => el.id),
        custom_groups: selectedGroupsIds,
      });

      if (result.error) {
        // dispatch(showBackendErrorNotifyNotify());
      } else {
        dispatch(showPublishedSuccessNotify({ entityName, is_published: Boolean(is_published) }));
        onPageExit();
      }
    } else {
      dispatch(showNotEnoughSelectedNotify());
    }
  }, [
    dispatch,
    is_published,
    groups,
    selectedGroupsIds,
    onUpdatePublication,
    isForAll,
    isForFutureGroups,
    isForNotClient,
    onPageExit,
  ]);

  const unpublish = useCallback(async () => {
    const result = await onUpdatePublication({
      is_published: false,
      is_for_future_groups: false,
      is_for_all: false,
      is_for_not_clients: false,
      is_for_without_groups: false,
      is_for_specialists: false,
      excluded_custom_groups: [],
      custom_groups: [],
    });

    if (result.error) {
      // dispatch(showBackendErrorNotifyNotify());
    } else {
      dispatch(showUnPublishedSuccessNotify({ entityName }));
      onPageExit();
    }
  }, [onUpdatePublication, dispatch, onPageExit]);

  const onRedirect = useCallback(() => resetSelectedGroupsIds([]), [resetSelectedGroupsIds]);

  const onPublicationButtonClick = useCallback(() => {
    if (isPublished) {
      setIsForFutureGroups(false);
      setIsForNotClient(false);
      setIsForAll(false);
      resetSelectedGroupsIds([]);
    }
    setIsPublished((isNowPublished) => !isNowPublished);
    doShowReadyButton();
  }, [
    isPublished,
    setIsForAll,
    setIsPublished,
    setIsForFutureGroups,
    setIsForNotClient,
    resetSelectedGroupsIds,
    doShowReadyButton,
  ]);

  return {
    groups,
    specialistsGroupData,
    withoutGroupGroupData,
    hasMoreGroups,
    loadGroups,
    groupsLoaderStatus,

    onPageExit: doCloseFormWithDialogModal({
      isShouldModalOpen: isInitStateHasChanged,
      onModalConfirm: onPageExit,
    }),

    onRedirect,
    publish,
    unpublish,

    isShouldShowReadyButton,
    isForAll,
    isForFutureGroups,
    isForNotClient,
    isPublishButtonDisabled,
    isPublished,

    onPublicationButtonClick,
    onForAllClick,
    onForFutureGroupsClick,
    onForNotClientClick,
    doShowReadyButton,
  };
};
